'use client';

import { cva } from 'class-variance-authority';
import {
  ReactNode,
  SelectHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

import cn from '../lib/cn';

const hoverClassName = 'hover:border-1.5 hover:border-content-brand-secondary';
const focusClassName =
  'focus-within:border-content-brand-secondary focus-within:border-1.5 ';
const filledClassName =
  'has-[input:not(:placeholder-shown)]:border-1.5 has-[input:not(:placeholder-shown)]:border-content-brand-secondary';

const wrapperVariants = cva(
  'relative flex justify-between w-full px-3 border border-dark-50 text-base font-normal gap-2.5 items-center bg-white group shadow-[1px_1px_2px_0_rgba(0,0,0,0.05)] transition-all rounded-lg',
  {
    variants: {
      size: {
        sm: 'h-8 rounded-lg',
        md: 'h-10 rounded-lg',
        lg: 'h-12 rounded-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

const selectVariants = cva(
  'absolute bg-white border rounded-lg z-10 left-0 w-full shadow-[0px_2px_10px_0px_rgba(0,0,0,0.1),0px_-1px_2px_0px_rgba(0,0,0,0.05)] p-1.5 text-base font-normal border border-dark-50 focus:outline-none focus:border-none focus:shadow-none focus:ring-0 placeholder:leading-normal placeholder:text-dark-200 disabled:pointer-events-none',
  {
    variants: {
      size: {
        sm: 'placeholder:text-sm text-sm',
        md: 'placeholder:text-base text-base',
        lg: 'placeholder:text-lg text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export type SelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> & {
  options: Array<string | SelectOption>;
  leftSection?: ReactNode;
  rightSection?: React.ReactNode;
  placeholder?: string;
  classNames?: {
    wrapper?: string;
    select?: string;
    error?: string;
    leftSection?: string;
  };
  error?: boolean;
  size?: 'sm' | 'md' | 'lg';
  value?: string;
  onValueChange?: (value: string) => void;
};

const getClassName = ({
  error,
  disabled,
}: {
  error?: boolean;
  disabled?: boolean;
}) => {
  if (disabled) return 'cursor-not-allowed border-0.5 bg-dark-10 text-dark-200';
  if (error)
    return 'border-red-500 border-1.5 shadow-[0_0_4px_0_rgba(var(--color-red-500))]';
  return hoverClassName + ' ' + focusClassName + ' ' + filledClassName;
};

const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      options,
      leftSection,
      rightSection,
      classNames,
      className,
      error,
      placeholder = 'Select an option',
      size = 'md',
      disabled,
      value,
      onValueChange,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState(value || '');
    const [isDropUp, setIsDropUp] = useState(false);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // Sync internal state with external value prop
    useEffect(() => {
      if (value !== undefined) {
        setSelected(value);
      }
    }, [value]);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const toggleDropdown = () => {
      if (!disabled) {
        if (!isOpen && wrapperRef.current) {
          const rect = wrapperRef.current.getBoundingClientRect();
          const isAtBottom = rect.bottom + 200 > window.innerHeight;
          setIsDropUp(isAtBottom);
        }
        setIsOpen((prev) => !prev);
      }
    };

    const handleSelect = (option: string | SelectOption) => {
      const optionValue = typeof option === 'string' ? option : option.value;
      setSelected(optionValue);
      setIsOpen(false);

      // Call the change handler
      onValueChange?.(optionValue);
    };

    const handleOptionClick = (
      event: React.MouseEvent,
      option: string | SelectOption,
    ) => {
      event.preventDefault();
      event.stopPropagation();
      handleSelect(option);
    };

    // Helper function to normalize options
    const normalizeOption = (option: string | SelectOption): SelectOption => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      return option;
    };

    // Find the selected option to display
    const getSelectedOption = (): SelectOption | null => {
      if (!selected) return null;

      for (const option of options) {
        const normalizedOption = normalizeOption(option);
        if (normalizedOption.value === selected) {
          return normalizedOption;
        }
      }
      return null;
    };

    const selectedOption = getSelectedOption();

    return (
      <div
        className={cn(
          wrapperVariants({ size }),
          getClassName({ error, disabled }),
          classNames?.wrapper,
          className,
        )}
        ref={(node) => {
          if (typeof ref === 'function') ref(node);
          wrapperRef.current = node;
        }}
        onClick={toggleDropdown}
      >
        <div className="cursor-pointer flex gap-2 items-center">
          {leftSection}
          {selectedOption ? (
            <div className="flex items-center gap-2">
              {selectedOption.icon && (
                <div className="flex items-center justify-center">
                  {selectedOption.icon}
                </div>
              )}
              <span>{selectedOption.label}</span>
            </div>
          ) : (
            <span>{placeholder}</span>
          )}
        </div>
        {rightSection}
        {isOpen && (
          <ul
            className={cn(
              selectVariants({ size }),
              isDropUp ? 'bottom-full mb-1' : 'top-full mt-1',
            )}
          >
            {options && options.length > 0 ? (
              options.map((option: string | SelectOption) => {
                const normalizedOption = normalizeOption(option);
                return (
                  <li
                    key={normalizedOption.value}
                    onClick={(event) => handleOptionClick(event, option)}
                    className="p-2 hover:bg-black/5 cursor-pointer rounded-[4px]"
                  >
                    <div className="flex items-center gap-2">
                      {normalizedOption.icon && (
                        <div className="flex items-center justify-center">
                          {normalizedOption.icon}
                        </div>
                      )}
                      <span>{normalizedOption.label}</span>
                    </div>
                  </li>
                );
              })
            ) : (
              <li
                className={cn(
                  'p-2 text-content-primary cursor-default rounded-[4px]',
                  size === 'sm'
                    ? 'text-sm'
                    : size === 'lg'
                      ? 'text-lg'
                      : 'text-base',
                )}
              >
                No options found
              </li>
            )}
          </ul>
        )}
        <div className="flex w-fit max-w-fit flex-1 items-end cursor-pointer text-end">
          <svg
            className={cn(
              'flex w-fit max-w-fit flex-1 items-end cursor-pointer transition-transform duration-200',
              isOpen ? 'rotate-180' : 'rotate-0',
            )}
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>
    );
  },
);
Select.displayName = 'Select';

/**
 * Enhanced Select Component
 *
 * Supports both simple string options and complex options with icons:
 *
 * @example
 * // Simple string options (backward compatible)
 * <Select
 *   options={['Users', 'Wallets']}
 *   value={value}
 *   onValueChange={onChange}
 * />
 *
 * @example
 * // Options with icons
 * import { User2, DollarSign } from 'lucide-react';
 *
 * const optionsWithIcons = [
 *   { value: 'users', label: 'Users', icon: <User2 size={16} /> },
 *   { value: 'revenue', label: 'Revenue', icon: <DollarSign size={16} /> }
 * ];
 *
 * <Select
 *   options={optionsWithIcons}
 *   value={value}
 *   onValueChange={onChange}
 * />
 */

export default Select;
